import { useState, useEffect, useContext } from "react";
import { Star, Moon, Sun, Sparkles, RefreshCw } from "lucide-react";
import axios from "axios";
import { API, AuthContext } from "../App";

// Tarot Card Data
const TAROT_CARDS = [
  { name: "The Fool", meaning: "New beginnings, innocence, spontaneity, free spirit", reversed: "Recklessness, risk-taking, negligence" },
  { name: "The Magician", meaning: "Willpower, desire, creation, manifestation", reversed: "Trickery, illusions, out of touch" },
  { name: "The High Priestess", meaning: "Intuition, sacred knowledge, divine feminine", reversed: "Secrets, withdrawal, silence" },
  { name: "The Empress", meaning: "Fertility, femininity, beauty, nature, abundance", reversed: "Creative block, dependence on others" },
  { name: "The Emperor", meaning: "Authority, structure, control, father figure", reversed: "Tyranny, rigidity, coldness" },
  { name: "The Hierophant", meaning: "Spiritual wisdom, tradition, conformity", reversed: "Rebellion, subversiveness, new approaches" },
  { name: "The Lovers", meaning: "Love, harmony, relationships, values alignment", reversed: "Disharmony, imbalance, misalignment" },
  { name: "The Chariot", meaning: "Control, willpower, success, determination", reversed: "Lack of control, aggression, obstacles" },
  { name: "Strength", meaning: "Courage, patience, compassion, inner strength", reversed: "Self-doubt, weakness, insecurity" },
  { name: "The Hermit", meaning: "Soul searching, introspection, inner guidance", reversed: "Isolation, loneliness, withdrawal" },
  { name: "Wheel of Fortune", meaning: "Good luck, karma, life cycles, destiny", reversed: "Bad luck, resistance to change" },
  { name: "Justice", meaning: "Fairness, truth, cause and effect, law", reversed: "Unfairness, lack of accountability" },
  { name: "The Hanged Man", meaning: "Pause, surrender, letting go, new perspectives", reversed: "Delays, resistance, stalling" },
  { name: "Death", meaning: "Endings, change, transformation, transition", reversed: "Resistance to change, unable to move on" },
  { name: "Temperance", meaning: "Balance, moderation, patience, purpose", reversed: "Imbalance, excess, lack of purpose" },
  { name: "The Devil", meaning: "Shadow self, attachment, addiction, restriction", reversed: "Releasing limiting beliefs, freedom" },
  { name: "The Tower", meaning: "Sudden change, upheaval, chaos, revelation", reversed: "Personal transformation, fear of change" },
  { name: "The Star", meaning: "Hope, faith, purpose, renewal, spirituality", reversed: "Lack of faith, despair, disconnection" },
  { name: "The Moon", meaning: "Illusion, fear, anxiety, subconscious, intuition", reversed: "Release of fear, repressed emotion" },
  { name: "The Sun", meaning: "Positivity, fun, warmth, success, vitality", reversed: "Inner child, feeling down, overly optimistic" },
  { name: "Judgement", meaning: "Judgement, rebirth, inner calling, absolution", reversed: "Self-doubt, refusal of self-examination" },
  { name: "The World", meaning: "Completion, accomplishment, travel, integration", reversed: "Incompleteness, no closure" }
];

// Zodiac Signs
const ZODIAC_SIGNS = [
  { sign: "Aries", symbol: "♈", dates: "Mar 21 - Apr 19", element: "Fire" },
  { sign: "Taurus", symbol: "♉", dates: "Apr 20 - May 20", element: "Earth" },
  { sign: "Gemini", symbol: "♊", dates: "May 21 - Jun 20", element: "Air" },
  { sign: "Cancer", symbol: "♋", dates: "Jun 21 - Jul 22", element: "Water" },
  { sign: "Leo", symbol: "♌", dates: "Jul 23 - Aug 22", element: "Fire" },
  { sign: "Virgo", symbol: "♍", dates: "Aug 23 - Sep 22", element: "Earth" },
  { sign: "Libra", symbol: "♎", dates: "Sep 23 - Oct 22", element: "Air" },
  { sign: "Scorpio", symbol: "♏", dates: "Oct 23 - Nov 21", element: "Water" },
  { sign: "Sagittarius", symbol: "♐", dates: "Nov 22 - Dec 21", element: "Fire" },
  { sign: "Capricorn", symbol: "♑", dates: "Dec 22 - Jan 19", element: "Earth" },
  { sign: "Aquarius", symbol: "♒", dates: "Jan 20 - Feb 18", element: "Air" },
  { sign: "Pisces", symbol: "♓", dates: "Feb 19 - Mar 20", element: "Water" }
];

// Horoscope messages
const HOROSCOPE_MESSAGES = [
  "Today brings unexpected opportunities. Stay open to new experiences and trust your instincts.",
  "Your creativity is at an all-time high. Use this energy to pursue your passions.",
  "A meaningful conversation awaits you. Listen carefully and speak from the heart.",
  "Financial matters look favorable. Consider making that investment you've been pondering.",
  "Love is in the air! Open your heart to romantic possibilities today.",
  "Focus on self-care and wellness. Your body is telling you to slow down.",
  "An old friend may reach out. Reconnecting will bring joy and nostalgia.",
  "Career advancement is on the horizon. Your hard work is about to pay off.",
  "Trust your intuition in matters of the heart. Your gut feeling is right.",
  "A surprise awaits you today. Embrace the unexpected with enthusiasm.",
  "Your leadership skills will be recognized. Step up and take charge.",
  "Travel plans may materialize unexpectedly. Be ready for adventure.",
  "Family matters require your attention. Show your loved ones you care.",
  "A creative project will gain momentum. Stay focused and dedicated.",
  "New friendships are forming. Be open to meeting different people."
];

// Fortune messages
const FORTUNES = [
  "A great adventure awaits you in the near future.",
  "Your kindness will be rewarded tenfold.",
  "Success is closer than you think - keep pushing forward.",
  "An unexpected gift will brighten your day soon.",
  "Your dreams are valid and within reach.",
  "A new opportunity will knock on your door this week.",
  "Love will find you when you least expect it.",
  "Your creativity will lead to great success.",
  "A long-lost friend will reconnect with you.",
  "Financial abundance is flowing your way.",
  "Trust your instincts - they will guide you well.",
  "A positive change is coming to your life.",
  "Your hard work will soon be recognized.",
  "Someone admires you from afar.",
  "The universe is aligning in your favor.",
  "A surprise journey awaits you.",
  "Your patience will be rewarded greatly.",
  "New beginnings bring new blessings."
];

const AstrologyPanel = ({ roomId }) => {
  const { token } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState("tarot");
  const [drawnCards, setDrawnCards] = useState([]);
  const [selectedSign, setSelectedSign] = useState(null);
  const [fortune, setFortune] = useState(null);
  const [luckyNumbers, setLuckyNumbers] = useState([]);
  const [isRevealing, setIsRevealing] = useState(false);

  // Live daily horoscopes + fortunes fetched from the backend on mount.
  // Backend generates fresh content via the Emergent LLM key once per UTC day
  // and caches the result so subsequent reads are instant.
  const [dailyData, setDailyData] = useState({ horoscopes: {}, fortunes: [], date: null });
  const [dailyLoaded, setDailyLoaded] = useState(false);

  useEffect(() => {
    if (roomId !== "astrology-room" || !token) return;
    let cancelled = false;
    axios.get(`${API}/astrology/daily`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (cancelled) return;
        setDailyData(res.data || { horoscopes: {}, fortunes: [], date: null });
        setDailyLoaded(true);
      })
      .catch(err => {
        console.error("Failed to load daily astrology:", err);
        setDailyLoaded(true); // Still flip the flag so the panel renders something
      });
    return () => { cancelled = true; };
  }, [roomId, token]);

  // Only show in astrology room
  if (roomId !== "astrology-room") return null;

  // Draw tarot cards
  const drawTarotCards = (count = 3) => {
    setIsRevealing(true);
    setDrawnCards([]);
    
    setTimeout(() => {
      const shuffled = [...TAROT_CARDS].sort(() => Math.random() - 0.5);
      const drawn = shuffled.slice(0, count).map(card => ({
        ...card,
        isReversed: Math.random() > 0.7 // 30% chance of reversed
      }));
      setDrawnCards(drawn);
      setIsRevealing(false);
    }, 1500);
  };

  // Get horoscope
  const getHoroscope = (sign) => {
    setSelectedSign(sign);
  };

  // Get fortune from today's freshly-generated pool
  const getFortune = () => {
    setFortune(null);
    const pool = (dailyData.fortunes && dailyData.fortunes.length > 0)
      ? dailyData.fortunes
      : FORTUNES;
    setTimeout(() => {
      setFortune(pool[Math.floor(Math.random() * pool.length)]);
    }, 1000);
  };

  // Generate lucky numbers
  const generateLuckyNumbers = () => {
    setLuckyNumbers([]);
    setTimeout(() => {
      const numbers = [];
      while (numbers.length < 6) {
        const num = Math.floor(Math.random() * 49) + 1;
        if (!numbers.includes(num)) numbers.push(num);
      }
      setLuckyNumbers(numbers.sort((a, b) => a - b));
    }, 800);
  };

  // Get today's horoscope text for the selected sign from backend-provided daily data.
  // Falls back to the static rotating list only if the backend call hasn't returned yet.
  const getHoroscopeMessage = () => {
    if (!selectedSign) return "";
    const todayText = dailyData?.horoscopes?.[selectedSign.sign];
    if (todayText) return todayText;
    // Fallback while loading: rotate daily so it doesn't read identically
    const today = new Date().toDateString();
    const seed = selectedSign.sign.length + today.length;
    return HOROSCOPE_MESSAGES[seed % HOROSCOPE_MESSAGES.length];
  };

  return (
    <div className="retro-window mb-2" data-testid="astrology-panel">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900 via-indigo-900 to-purple-900 text-white px-3 py-2">
        <div className="flex items-center gap-2">
          <Moon className="w-5 h-5" />
          <span className="font-bold">🔮 ASTROLOGY & THE FUTURE</span>
          <Sparkles className="w-4 h-4 animate-pulse text-yellow-300" />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b-2 border-purple-800 bg-purple-950">
        {[
          { id: "tarot", label: "🃏 Tarot" },
          { id: "horoscope", label: "⭐ Horoscope" },
          { id: "fortune", label: "🔮 Fortune" },
          { id: "lucky", label: "🍀 Lucky #s" }
        ].map(tab => (
          <button
            key={tab.id}
            className={`flex-1 px-3 py-2 text-xs font-bold transition-all ${
              activeTab === tab.id
                ? 'bg-purple-700 text-yellow-300 border-b-2 border-yellow-400'
                : 'text-purple-300 hover:bg-purple-800'
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="p-3 bg-gradient-to-b from-purple-950 to-black min-h-[300px]">
        {/* TAROT TAB */}
        {activeTab === "tarot" && (
          <div>
            <div className="text-center mb-4">
              <p className="text-purple-300 text-sm mb-3">Ask a question, then draw your cards...</p>
              <div className="flex justify-center gap-2 mb-4">
                <button
                  className="retro-button bg-purple-700 text-white px-4 py-2 text-sm hover:bg-purple-600"
                  onClick={() => drawTarotCards(1)}
                  disabled={isRevealing}
                >
                  Draw 1 Card
                </button>
                <button
                  className="retro-button bg-purple-700 text-white px-4 py-2 text-sm hover:bg-purple-600"
                  onClick={() => drawTarotCards(3)}
                  disabled={isRevealing}
                >
                  3 Card Spread
                </button>
              </div>
            </div>

            {isRevealing && (
              <div className="text-center py-8">
                <div className="text-4xl animate-spin mb-2">🔮</div>
                <p className="text-purple-300 animate-pulse">The cards are being revealed...</p>
              </div>
            )}

            {drawnCards.length > 0 && !isRevealing && (
              <div className="flex justify-center gap-3 flex-wrap">
                {drawnCards.map((card, index) => (
                  <div
                    key={index}
                    className={`bg-gradient-to-b from-purple-800 to-purple-950 border-2 border-yellow-500 rounded-lg p-3 w-40 text-center ${
                      card.isReversed ? 'transform rotate-180' : ''
                    }`}
                  >
                    <div className={card.isReversed ? 'transform rotate-180' : ''}>
                      <div className="text-3xl mb-2">🃏</div>
                      <h4 className="text-yellow-300 font-bold text-sm mb-1">{card.name}</h4>
                      {card.isReversed && <span className="text-red-400 text-xs">(Reversed)</span>}
                      <p className="text-purple-200 text-xs mt-2">
                        {card.isReversed ? card.reversed : card.meaning}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {drawnCards.length === 0 && !isRevealing && (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">🃏</div>
                <p className="text-purple-400 text-sm">Click above to draw your tarot cards</p>
              </div>
            )}
          </div>
        )}

        {/* HOROSCOPE TAB */}
        {activeTab === "horoscope" && (
          <div>
            <p className="text-purple-300 text-sm text-center mb-3">Select your zodiac sign:</p>
            
            <div className="grid grid-cols-4 gap-2 mb-4">
              {ZODIAC_SIGNS.map((zodiac) => (
                <button
                  key={zodiac.sign}
                  className={`p-2 rounded text-center transition-all ${
                    selectedSign?.sign === zodiac.sign
                      ? 'bg-yellow-500 text-black'
                      : 'bg-purple-800/50 text-purple-200 hover:bg-purple-700'
                  }`}
                  onClick={() => getHoroscope(zodiac)}
                >
                  <div className="text-2xl">{zodiac.symbol}</div>
                  <div className="text-xs font-bold">{zodiac.sign}</div>
                </button>
              ))}
            </div>

            {selectedSign && (
              <div className="bg-purple-900/50 rounded-lg p-4 border border-purple-500">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-4xl">{selectedSign.symbol}</span>
                  <div>
                    <h3 className="text-yellow-300 font-bold text-lg">{selectedSign.sign}</h3>
                    <p className="text-purple-300 text-xs">{selectedSign.dates}</p>
                    <p className="text-purple-400 text-xs">Element: {selectedSign.element}</p>
                  </div>
                </div>
                <div className="bg-black/30 p-3 rounded">
                  <p className="text-purple-200 text-sm italic">"{getHoroscopeMessage()}"</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* FORTUNE TAB */}
        {activeTab === "fortune" && (
          <div className="text-center">
            <div className="text-6xl mb-4">🔮</div>
            <p className="text-purple-300 text-sm mb-4">Gaze into the crystal ball...</p>
            
            <button
              className="retro-button bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 text-sm hover:from-purple-500 hover:to-pink-500 mb-4"
              onClick={getFortune}
            >
              <Sparkles className="w-4 h-4 inline mr-2" />
              Reveal My Fortune
            </button>

            {fortune && (
              <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 rounded-lg p-6 border border-purple-400 mt-4 animate-fade-in">
                <Star className="w-8 h-8 mx-auto text-yellow-400 mb-3" />
                <p className="text-yellow-200 text-lg italic">"{fortune}"</p>
              </div>
            )}
          </div>
        )}

        {/* LUCKY NUMBERS TAB */}
        {activeTab === "lucky" && (
          <div className="text-center">
            <div className="text-6xl mb-4">🍀</div>
            <p className="text-purple-300 text-sm mb-4">Discover your lucky numbers today...</p>
            
            <button
              className="retro-button bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 text-sm hover:from-green-500 hover:to-emerald-500 mb-4"
              onClick={generateLuckyNumbers}
            >
              <RefreshCw className="w-4 h-4 inline mr-2" />
              Generate Lucky Numbers
            </button>

            {luckyNumbers.length > 0 && (
              <div className="mt-4">
                <p className="text-purple-300 text-xs mb-3">YOUR LUCKY NUMBERS:</p>
                <div className="flex justify-center gap-3 flex-wrap">
                  {luckyNumbers.map((num, index) => (
                    <div
                      key={index}
                      className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-black font-bold text-lg shadow-lg animate-bounce"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      {num}
                    </div>
                  ))}
                </div>
                <p className="text-purple-400 text-xs mt-4">Use these numbers wisely! 🌟</p>
              </div>
            )}

            {luckyNumbers.length === 0 && (
              <div className="bg-purple-900/30 rounded-lg p-6 mt-4">
                <p className="text-purple-400 text-sm">Click the button to reveal your 6 lucky numbers</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AstrologyPanel;
